import {computed} from "vue";
import {cacheKey} from "@/lib/cache";
import useSwr from "@/core/composables/swr/useSwr";
import {appointmentTypeApi} from "@/container";
import useOrganizationId from "@/core/composables/useOrganizationId";
import {AppointmentTypeIndexOptions} from "@/core/http/resources/AppointmentTypeResource";

/**
 * Composable to fetch the appointment types of the current organization.
 * Templates hidden by the pharmacy are left out unless `withHidden` is set.
 *
 * Hidden templates are always fetched and filtered here, so every screen
 * shares one cache entry and a mutation on it reaches them all.
 */
const useAppointmentTypes = (options: AppointmentTypeIndexOptions = {}) => {
    const organizationId = useOrganizationId();

    /**
     * The SWR key.
     */
    const key = computed(() => {
        if (!organizationId.value) {
            return null;
        }

        return cacheKey(`organizations/${organizationId.value}/appointment-types`, {with_hidden: 1});
    });

    /**
     * Fetch the appointment types from the API.
     */
    const fetchData = () => {
        if (!organizationId.value) {
            return Promise.resolve([]);
        }

        return appointmentTypeApi().index(organizationId.value, {withHidden: true});
    };

    const {data: allTypes, isLoading, mutate} = useSwr(
        () => key.value,
        () => fetchData()
    );

    const data = computed(() => {
        return options.withHidden ? allTypes.value : allTypes.value?.filter((type) => !type.is_hidden);
    });

    return {
        data,
        isLoading,
        mutate,
        organizationId
    };
};

export default useAppointmentTypes;
